import React from 'react';
import { FileText, Plus, Search, Eye, Download, Printer, Loader2, ArrowRightLeft, Truck, Edit3, Check, Wrench } from 'lucide-react';
import Pagination from '../ui/Pagination';
import SortableHeader from '../ui/SortableHeader';
import { filterBranchesForUser } from '../../utils/branchScope';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  Draft: 'bg-gray-100 text-gray-600',
  Confirmed: 'bg-green-100 text-green-700',
  'Ready to Dispatch': 'bg-amber-100 text-amber-700',
  Dispatched: 'bg-blue-100 text-blue-700',
  Cancelled: 'bg-red-100 text-red-700',
};

const OCListView = ({
  ocs, loading, totalItems, search, statusFilter, branchFilter, branches, page, totalPages, pageSize,
  selectedOCs, bulkLoading, canMarkDispatch, canAddInvoice, canAmend,
  onSearchChange, onStatusChange, onBranchChange, onPageChange, onPageSizeChange,
  onSelectOC, onSelectAll, onToggleSelect,
  onView, onDownload, onPrint, onEditDraft, onConfirmDraft, onAmend, onDispatch, onInvoice,
  onBulkUpdate, onCreateDirect, onFromQuote,
  sortConfig, onSort,
  adminTools,
}) => {
  return (
    <div data-testid="oc-list">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Order Confirmations</h2>
          <p className="text-[11px] text-gray-500">{totalItems} total OCs</p>
        </div>
        <div className="flex items-center gap-2">
          {adminTools?.onProductBindingAudit && (
            <button data-testid="oc-binding-audit-btn" onClick={adminTools.onProductBindingAudit}
              title="Admin: audit & heal stale product references on OCs (resolves 'I have stock but it says I don't' false-zeros)"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-amber-200 text-amber-700 hover:bg-amber-50">
              <Wrench className="w-3.5 h-3.5" /> Audit Bindings
            </button>
          )}
          <button data-testid="oc-from-quote-btn" onClick={onFromQuote} className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
            <ArrowRightLeft className="w-3.5 h-3.5" /> From Quotation
          </button>
          <button data-testid="oc-direct-btn" onClick={onCreateDirect} className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg" style={{ backgroundColor: '#24549c' }}>
            <Plus className="w-3.5 h-3.5" /> Direct OC
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input data-testid="oc-search" value={search} onChange={e => onSearchChange(e.target.value)} placeholder="Search by OC#, customer, project..."
            className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
          {['all', 'Draft', 'Confirmed', 'Ready to Dispatch', 'Dispatched', 'Cancelled'].map(s => (
            <button key={s} onClick={() => onStatusChange(s)} data-testid={`filter-${s.replace(/\s/g, '-').toLowerCase()}`}
              className={`px-2.5 py-1 text-[10px] font-semibold rounded-md transition-colors ${statusFilter === s ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
              {s === 'all' ? 'All' : s}
            </button>
          ))}
        </div>
        <select data-testid="oc-branch-filter" value={branchFilter} onChange={e => onBranchChange(e.target.value)}
          className="px-2.5 py-1.5 text-[11px] font-medium border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-200">
          <option value="">All Branches</option>
          {filterBranchesForUser(branches).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      {/* Bulk Action Bar */}
      {selectedOCs.size > 0 && (
        <div className="flex items-center gap-3 mb-3 p-3 rounded-xl border border-blue-200 bg-blue-50/50" data-testid="bulk-action-bar">
          <span className="text-xs font-semibold text-blue-700">{selectedOCs.size} selected</span>
          <button onClick={() => onSelectOC(new Set())} className="text-[10px] text-gray-500 hover:underline">Deselect all</button>
          <div className="ml-auto flex gap-2">
            {['Confirmed', 'Dispatched', 'Cancelled'].map(s => (
              <button key={s} data-testid={`bulk-${s.replace(/\s/g, '-').toLowerCase()}`}
                onClick={() => onBulkUpdate(s)} disabled={bulkLoading}
                className={`px-2.5 py-1 text-[10px] font-semibold rounded-lg transition-colors disabled:opacity-50 ${
                  s === 'Cancelled' ? 'bg-red-100 text-red-700 hover:bg-red-200' :
                  s === 'Dispatched' ? 'bg-green-100 text-green-700 hover:bg-green-200' :
                  'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >{bulkLoading ? '...' : s}</button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin" style={{ color: '#24549c' }} /></div>
      ) : ocs.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-10 text-center">
          <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="text-sm font-semibold text-gray-900 mb-1">No Order Confirmations</p>
          <p className="text-xs text-gray-500">Create your first OC using the buttons above</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="text-[10px] font-bold text-gray-500 uppercase" style={{ backgroundColor: '#f8f9fb' }}>
                <th className="text-left px-3 py-3 w-8">
                  <input type="checkbox" data-testid="oc-select-all"
                    checked={selectedOCs.size > 0 && ocs.every(oc => selectedOCs.has(oc.id))}
                    onChange={e => onSelectAll(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                </th>
                <SortableHeader label="OC #" sortKey="oc_number" currentSort={sortConfig} onSort={onSort} />
                <SortableHeader label="Type" />
                <SortableHeader label="Customer" />
                <SortableHeader label="Project" />
                <SortableHeader label="Date" sortKey="created_at" currentSort={sortConfig} onSort={onSort} />
                <SortableHeader label="Total" />
                <SortableHeader label="Status" />
                <SortableHeader label="Actions" />
              </tr>
            </thead>
            <tbody>
              {ocs.map(oc => (
                <tr key={oc.id} className="border-b border-gray-50 hover:bg-gray-50/80 transition-colors">
                  <td className="px-3 py-3">
                    <input type="checkbox" data-testid={`oc-check-${oc.id}`}
                      checked={selectedOCs.has(oc.id)}
                      onChange={() => onToggleSelect(oc.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  </td>
                  <td className="px-4 py-3 text-xs font-mono font-bold" style={{ color: '#24549c' }}>{oc.oc_number}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${oc.oc_type === 'signage' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>{oc.oc_type === 'signage' ? 'Signage' : 'Generic'}</span></td>
                  <td className="px-4 py-3 text-xs font-medium text-gray-900">{oc.customer?.name}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{oc.site_project_code ? <span className="font-mono text-blue-600">{oc.site_project_code}</span> : (oc.project_name || '-')}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{new Date(oc.created_at).toLocaleDateString('en-IN')}</td>
                  <td className="px-4 py-3 text-xs font-bold" style={{ color: '#24549c' }}>INR {(oc.total || 0).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${statusColors[oc.status] || 'bg-gray-100 text-gray-600'}`}>{oc.status}</span>
                    {oc.tally_invoice_number && (
                      oc.invoice_document_url ? (
                        <a href={`${API_BASE}/api/files/view?path=${encodeURIComponent(oc.invoice_document_url)}`}
                          target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                          className="ml-1 text-[9px] text-emerald-600 font-mono hover:underline cursor-pointer" data-testid={`invoice-link-${oc.id}`}
                          title={`View Invoice: ${oc.tally_invoice_number}`}>
                          #{oc.tally_invoice_number}
                        </a>
                      ) : (
                        <span className="ml-1 text-[9px] text-emerald-600 font-mono">#{oc.tally_invoice_number}</span>
                      )
                    )}
                    {(oc.revision || 0) > 0 && <span className="ml-1 text-[9px] text-purple-600 font-bold">Rev-{oc.revision}</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button data-testid={`oc-view-${oc.id}`} onClick={() => onView(oc)} className="p-1 rounded hover:bg-blue-50" title="View"><Eye className="w-3.5 h-3.5 text-blue-600" /></button>
                      <button data-testid={`oc-download-${oc.id}`} onClick={() => onDownload(oc)} className="p-1 rounded hover:bg-gray-100" title="Download"><Download className="w-3.5 h-3.5 text-gray-500" /></button>
                      <button data-testid={`oc-print-${oc.id}`} onClick={() => onPrint(oc)} className="p-1 rounded hover:bg-gray-100" title="Print"><Printer className="w-3.5 h-3.5 text-gray-500" /></button>
                      {oc.status === 'Draft' && (
                        <>
                          <button data-testid={`oc-edit-${oc.id}`} onClick={() => onEditDraft(oc)} className="p-1 rounded hover:bg-gray-100" title="Edit Draft"><Edit3 className="w-3.5 h-3.5 text-gray-600" /></button>
                          <button data-testid={`oc-confirm-${oc.id}`} onClick={() => onConfirmDraft(oc.id)} className="p-1 rounded hover:bg-green-50" title="Confirm OC"><Check className="w-3.5 h-3.5 text-green-600" /></button>
                        </>
                      )}
                      {canAmend && ['Confirmed', 'Ready to Dispatch'].includes(oc.status) && (
                        <button data-testid={`oc-amend-${oc.id}`} onClick={() => onAmend(oc)} className="p-1 rounded hover:bg-purple-50" title="Amend OC"><Edit3 className="w-3.5 h-3.5 text-purple-600" /></button>
                      )}
                      {canMarkDispatch && oc.status === 'Confirmed' && (
                        <button data-testid={`oc-dispatch-${oc.id}`} onClick={() => onDispatch(oc)} className="p-1 rounded hover:bg-amber-50" title="Ready to Dispatch"><Truck className="w-3.5 h-3.5 text-amber-600" /></button>
                      )}
                      {canAddInvoice && oc.status === 'Ready to Dispatch' && (
                        <button data-testid={`oc-invoice-${oc.id}`} onClick={() => onInvoice(oc)} className="p-1 rounded hover:bg-blue-50" title={oc.tally_invoice_number ? `Invoice: ${oc.tally_invoice_number}` : 'Add Invoice'}>
                          <FileText className={`w-3.5 h-3.5 ${oc.tally_invoice_number ? 'text-emerald-600' : 'text-blue-600'}`} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {ocs.length > 0 && <Pagination page={page} totalPages={totalPages} total={totalItems} pageSize={pageSize} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} />}
    </div>
  );
};

export default OCListView;
